import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { Marque, MotMarque } from './composants/Marque.tsx';
import { useLibelles } from './i18n.tsx';
import { useEtatConnexion } from './reseau.ts';
import { useSession } from './etat.tsx';
import { Accueil } from './ecrans/Accueil.tsx';
import { Acces } from './ecrans/Acces.tsx';
import { Bienvenue } from './ecrans/Bienvenue.tsx';
import { Repertoire } from './ecrans/Repertoire.tsx';
import { FicheDetail } from './ecrans/FicheDetail.tsx';
import { Agenda } from './ecrans/Agenda.tsx';
import { Programme } from './ecrans/Programme.tsx';
import { Suggestions } from './ecrans/Suggestions.tsx';
import { EspaceAdmin } from './admin/EspaceAdmin.tsx';
import { Vitrine } from './ecrans/Vitrine.tsx';
import { Profil } from './ecrans/Profil.tsx';
import { BilletterieAchat } from './ecrans/BilletterieAchat.tsx';
import { BilletterieConfirmation } from './ecrans/BilletterieConfirmation.tsx';
import { CompteCreer, CompteConnexion } from './ecrans/CompteCreer.tsx';
import { EspaceCompte } from './ecrans/EspaceCompte.tsx';
import { PagePerso } from './ecrans/PagePerso.tsx';
import { EvenementPublic } from './ecrans/EvenementPublic.tsx';
import { Murs } from './ecrans/Murs.tsx';
import { Affiche } from './ecrans/Affiche.tsx';

/** Destinations de la barre basse. Une par grande intention, jamais plus de cinq. */
const DESTINATIONS = [
  { chemin: '/', cle: 'nav.accueil', defaut: 'Accueil', icone: 'accueil' },
  { chemin: '/suggestions', cle: 'nav.suggestions', defaut: 'Suggestions', icone: 'etincelle' },
  { chemin: '/participants', cle: 'nav.repertoire', defaut: 'Participants', icone: 'personnes' },
  { chemin: '/agenda', cle: 'nav.agenda', defaut: 'Agenda', icone: 'agenda' },
  { chemin: '/profil', cle: 'nav.profil', defaut: 'Mon profil', icone: 'profil' },
] as const;

const CHEMINS: Record<string, string> = {
  accueil: 'M4 11 12 4l8 7v8a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z',
  personnes: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7.5 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM2.5 19a6.5 6.5 0 0 1 13 0Zm14.4-6.5A5.5 5.5 0 0 1 21.5 19h-3.9a8 8 0 0 0-1.7-5 5.4 5.4 0 0 1 1-1.5Z',
  etincelle: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Zm6 9.5.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6Z',
  agenda: 'M7 2v2h10V2h2v2h1a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V2h2Zm13 8H4v9h16v-9Z',
  profil: 'M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2c-4.4 0-8 2.6-8 5.8V21h16v-1.2c0-3.2-3.6-5.8-8-5.8Z',
};

/** Les chemins de premier niveau que l'application occupe déjà : jamais un pseudo. */
const SEGMENTS_RESERVES = new Set([
  'api', 'compte', 'orga', 'billet', 'acces', 'bienvenue', 'participants', 'agenda', 'programme', 'evenements', 'mur', 'affiche',
  'suggestions', 'profil', 'sante', 'assets', 'admin', 'join', 'sw.js', 'manifest.webmanifest',
  'icones', 'images', 'robots.txt', 'sitemap.xml', 'favicon.ico',
]);

function Icone({ nom }: { nom: string }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path d={CHEMINS[nom]} fill="currentColor" />
    </svg>
  );
}

export function App() {
  const { t } = useLibelles();
  const { enLigne } = useEtatConnexion();
  const { moi, charge } = useSession();
  const localisation = useLocation();

  // Écrans hors coquille : accès (pas encore de session), bienvenue (session
  // toute neuve) et espace organisateur (identité et navigation distinctes).
  if (localisation.pathname.startsWith('/acces')) return <Acces />;
  if (localisation.pathname.startsWith('/orga')) return <EspaceAdmin />;
  // Espace personnel : identité et navigation distinctes de l'application
  // d'événement — on y arrive sans code et sans participation.
  if (localisation.pathname.startsWith('/compte')) {
    return (
      <Routes>
        <Route path="/compte/creer" element={<CompteCreer />} />
        <Route path="/compte/connexion" element={<CompteConnexion />} />
        {/* L'espace gère ses propres sous-écrans (tableau de bord, entreprise,
            événements, rencontres) derrière sa barre latérale. */}
        <Route path="/compte/*" element={<EspaceCompte />} />
      </Routes>
    );
  }

  // Billetterie publique : achetable sans compte, sans session — jamais
  // derrière la vitrine ni la coquille applicative.
  if (localisation.pathname.startsWith('/billet')) {
    return (
      <Routes>
        <Route path="/billet/confirmation" element={<BilletterieConfirmation />} />
        <Route path="/billet/:organisationSlug/:evenementSlug" element={<BilletterieAchat />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Les trois murs, projetables : eventmatch.pro/mur/<slug>. Publics.
  if (localisation.pathname.startsWith('/mur/')) {
    return <Murs slug={localisation.pathname.split('/')[2] ?? ''} />;
  }

  // L'affiche à imprimer : eventmatch.pro/affiche/<slug>. Compte requis (c'est
  // le QR de celui qui imprime) — l'API s'en assure.
  if (localisation.pathname.startsWith('/affiche/')) {
    return <Affiche slug={localisation.pathname.split('/')[2] ?? ''} />;
  }

  // La fiche publique d'un événement : eventmatch.pro/evenements/<slug>.
  // Publique, hors coquille, quel que soit l'état de session.
  if (localisation.pathname.startsWith('/evenements/')) {
    const slug = localisation.pathname.split('/')[2] ?? '';
    return <EvenementPublic slug={slug} rejoindreAuto={new URLSearchParams(localisation.search).get('rejoindre') === '1'} />;
  }

  // La page personnelle : eventmatch.pro/<pseudo> (+ /join). Publique, hors
  // coquille, quel que soit l'état de session. Les premiers segments déjà pris
  // par l'application ne peuvent pas être des pseudos (liste miroir côté API).
  const segments = localisation.pathname.split('/').filter(Boolean);
  if (
    (segments.length === 1 || (segments.length === 2 && segments[1] === 'join')) &&
    !SEGMENTS_RESERVES.has(segments[0]!.toLowerCase()) &&
    /^[a-z0-9][a-z0-9-]{2,29}$/i.test(segments[0]!)
  ) {
    return <PagePerso pseudo={segments[0]!.toLowerCase()} join={segments.length === 2} />;
  }

  // Sans session : la vitrine publique — c'est elle, la page d'accueil
  // d'eventmatch.pro. Le lien d'accès personnel (/acces?jeton=…) et l'espace
  // organisateur gardent leurs entrées directes ci-dessus.
  if (charge && !moi) return <Vitrine />;

  if (localisation.pathname.startsWith('/bienvenue')) return <Bienvenue />;

  return (
    <div className="coquille">
      <header className="entete">
        <div className="entete__marque">
          <Marque />
          <MotMarque />
        </div>
        <span className="entete__sponsor">{t('app.sponsor', 'Offert par Elite Lead')}</span>
      </header>

      {!enLigne && (
        <p className="bandeau-hors-ligne" role="status">
          {t('app.hors_ligne', 'Hors ligne — vous consultez la dernière version enregistrée')}
        </p>
      )}

      <main className="contenu" id="contenu">
        <Routes>
          <Route path="/" element={<Accueil />} />
          <Route path="/participants" element={<Repertoire />} />
          <Route path="/participants/:participantId" element={<FicheDetail />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/programme" element={<Programme />} />
          <Route path="/suggestions" element={<Suggestions />} />
          <Route path="/profil" element={<Profil />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <nav className="navigation" aria-label="Navigation principale">
        {DESTINATIONS.map((d) => (
          <NavLink
            key={d.chemin}
            to={d.chemin}
            end={d.chemin === '/'}
            className={({ isActive }) =>
              `navigation__lien cible${isActive ? ' navigation__lien--actif' : ''}`
            }
          >
            <Icone nom={d.icone} />
            <span className="navigation__texte">{t(d.cle, d.defaut)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
